import type {
	DailyCall,
	DailyEventObjectTranscriptionMessage,
} from '@daily-co/daily-js';

export const DAILY_TRANSCRIPTION_LANGUAGE = 'pt-BR';

export function ensureDailyCallTranscriptionStarted(
	callFrame: DailyCall | null,
	language: string = DAILY_TRANSCRIPTION_LANGUAGE,
): void {
	if (!callFrame || callFrame.isDestroyed()) return;

	try {
		callFrame.startTranscription({ language });
	} catch {
		/* A sala pode já estar transcrevendo via backend ou outro cliente. */
	}
}

export type SessionCallTranscriptionChatMessage = {
	id: string;
	participantId: string;
	speakerName: string;
	isLocalSpeaker: boolean;
	text: string;
	timestamp: string;
	/** Balão ainda recebendo fala ao vivo (mostra reticências). */
	isLive?: boolean;
	/** Momento em que o último trecho chegou (agrupamento por pausa real). */
	lastReceivedAtMs?: number;
	/** Quando o balão ao vivo foi fechado por pausa (para absorver atraso do Daily). */
	finalizedAtMs?: number;
};

export function resolveDailyTranscriptionSpeaker(
	callFrame: DailyCall | null,
	participantId: string,
): { speakerName: string; isLocalSpeaker: boolean } {
	if (!callFrame || callFrame.isDestroyed()) {
		return { speakerName: 'Participante', isLocalSpeaker: false };
	}

	const participants = callFrame.participants();
	const localSessionId = participants.local?.session_id;
	const isLocalSpeaker =
		!!localSessionId &&
		participantId.length > 0 &&
		participantId === localSessionId;

	const remote = Object.values(participants).find(
		(participant) => participant.session_id === participantId,
	);

	const speakerName =
		(isLocalSpeaker
			? participants.local?.user_name
			: remote?.user_name
		)?.trim() || 'Participante';

	return { speakerName, isLocalSpeaker };
}

export function createTranscriptionChatMessage(
	callFrame: DailyCall | null,
	event: DailyEventObjectTranscriptionMessage,
): SessionCallTranscriptionChatMessage | null {
	const text = event.text?.trim();
	if (!text) return null;

	const participantId = event.participantId?.trim() || 'unknown';
	const { speakerName, isLocalSpeaker } = resolveDailyTranscriptionSpeaker(
		callFrame,
		participantId,
	);

	const timestamp =
		event.timestamp instanceof Date
			? event.timestamp.toISOString()
			: event.timestamp ?? new Date().toISOString();

	return {
		id: `${participantId}-${timestamp}-${text.slice(0, 24)}`,
		participantId,
		speakerName,
		isLocalSpeaker,
		text,
		timestamp,
	};
}

/** Pausa sem fala da mesma pessoa antes de fechar o balão ao vivo. */
export const TRANSCRIPTION_CHAT_GROUP_PAUSE_MS = 4_000;

export const countTranscriptionChatWords = (text: string): number => {
	const trimmed = text.trim();
	return trimmed ? trimmed.split(/\s+/).length : 0;
};

/** Após fechar o balão, ainda reabre se o Daily mandar o próximo trecho com atraso. */
export const transcriptionLateChunkAfterFinalizeMs = (
	groupPauseMs = TRANSCRIPTION_CHAT_GROUP_PAUSE_MS,
) => groupPauseMs + 1_500;

export const mergeTranscriptionChatText = (
	previous: string,
	next: string,
): string => {
	const prev = previous.trim();
	const nextTrim = next.trim();
	if (!nextTrim) return prev;
	if (!prev) return nextTrim;
	if (nextTrim === prev) return prev;
	if (nextTrim.startsWith(prev)) return nextTrim;
	if (prev.startsWith(nextTrim)) return prev;
	if (prev.endsWith(nextTrim)) return prev;
	return `${prev} ${nextTrim}`;
};

export const appendTranscriptionChatMessage = (
	history: SessionCallTranscriptionChatMessage[],
	incoming: SessionCallTranscriptionChatMessage,
	receivedAtMs: number,
	groupPauseMs = TRANSCRIPTION_CHAT_GROUP_PAUSE_MS,
): SessionCallTranscriptionChatMessage[] => {
	const last = history.at(-1);
	const createLiveBubble = (
		message: SessionCallTranscriptionChatMessage,
	): SessionCallTranscriptionChatMessage => ({
		...message,
		id: `${message.participantId}-${receivedAtMs}`,
		isLive: true,
		lastReceivedAtMs: receivedAtMs,
		finalizedAtMs: undefined,
	});

	if (!last) {
		return [createLiveBubble(incoming)];
	}

	const sameSpeaker = last.participantId === incoming.participantId;
	const historyWithInactive = history.map((message) => ({
		...message,
		isLive: false,
	}));

	const mergeIntoLast = (
		target: SessionCallTranscriptionChatMessage,
	): SessionCallTranscriptionChatMessage[] => [
		...historyWithInactive.slice(0, -1),
		{
			...target,
			text: mergeTranscriptionChatText(target.text, incoming.text),
			isLive: true,
			lastReceivedAtMs: receivedAtMs,
			finalizedAtMs: undefined,
		},
	];

	if (!sameSpeaker) {
		return [...historyWithInactive, createLiveBubble(incoming)];
	}

	// Mesmo falante com balão ainda ao vivo: ignora atraso entre eventos do Daily.
	if (last.isLive) {
		return mergeIntoLast(last);
	}

	// Balão já fechou; reabre só se o trecho chegou logo depois (latência do Daily).
	const sinceFinalizeMs =
		receivedAtMs -
		(last.finalizedAtMs ?? last.lastReceivedAtMs ?? receivedAtMs);

	if (
		sinceFinalizeMs <= transcriptionLateChunkAfterFinalizeMs(groupPauseMs)
	) {
		return mergeIntoLast(last);
	}

	return [...historyWithInactive, createLiveBubble(incoming)];
};

export const finalizeLiveTranscriptionChatMessages = (
	history: SessionCallTranscriptionChatMessage[],
	finalizedAtMs = Date.now(),
): SessionCallTranscriptionChatMessage[] => {
	const last = history.at(-1);
	if (!last?.isLive) return history;

	return [...history.slice(0, -1), { ...last, isLive: false, finalizedAtMs }];
};
